import { useEffect } from 'react';

interface WakeLockSentinelLike {
  release(): Promise<void>;
}

/**
 * Keeps a phone's or handheld's screen on while the warehouse screens are open (a picker walking
 * with the phone in hand should not have to unlock it at every bin). Touch devices only, so the
 * office computers still sleep; released when the page is hidden, taken again when it is back.
 */
export function useScreenWakeLock(active: boolean): void {
  useEffect(() => {
    const wakeLock = (navigator as Navigator & { wakeLock?: { request(type: 'screen'): Promise<WakeLockSentinelLike> } }).wakeLock;
    if (!active || !wakeLock || !window.matchMedia?.('(pointer: coarse)').matches) return;
    let sentinel: WakeLockSentinelLike | null = null;
    let stopped = false;
    const take = () => {
      if (document.visibilityState !== 'visible' || stopped) return;
      wakeLock
        .request('screen')
        .then((s) => {
          if (stopped) void s.release();
          else sentinel = s;
        })
        .catch(() => undefined);
    };
    take();
    document.addEventListener('visibilitychange', take);
    return () => {
      stopped = true;
      document.removeEventListener('visibilitychange', take);
      void sentinel?.release().catch(() => undefined);
    };
  }, [active]);
}
